/**
 * RCR — Relative Capital Return Analysis.
 *
 * The neighbourhood cap rates this listing is measured against on one side, the ratios
 * themselves on the other. RCR of 1.00 is exactly average.
 *
 * ONE component for two pages, deliberately. The analyst and the client have to be looking at
 * the same seven figures computed the same way, and the previous arrangement -- the client
 * page owning the card and the analyst page carrying two of the ratios in a corner of the top
 * box -- is how they drifted apart in the first place.
 *
 * The difference between the two is `onVisibilityChange`:
 *
 *   present  ANALYST. Every row renders, each with a tick box. Unticked rows stay on screen,
 *            dimmed, because the analyst has to be able to put one back.
 *   absent   CLIENT. Unticked rows are not rendered at all, and a group with nothing left in
 *            it loses its heading rather than printing an empty column.
 */

const fmtPct = (v) => (v || v === 0 ? `${Number(v).toFixed(2)}%` : null);
const fmtRatio = (cap, base) => (cap > 0 && base > 0 ? (cap / base).toFixed(2) : null);
const bedLabel = (b) => (b === 0 ? "Studios" : b ? `${b}BR` : "this size");

// Absent means visible. A listing saved before any of this existed has no `rcr_visibility` at
// all, and so does one the analyst never touched -- both must show the full card.
export const isRcrRowVisible = (visibility, key) => visibility?.[key] !== false;

export default function RCRAnalysis({
  capRate,
  neighborhoodAvg,
  neighborhoodWeighted,
  neighborhoodMedian,
  assetClass,
  assetClassIsFallback = false,
  assetClassCount = null,
  bedrooms,
  visibility,
  onVisibilityChange,
  narrow = false,
}) {
  const editable = typeof onVisibilityChange === "function";
  const beds = bedLabel(bedrooms);

  const groups = [
    {
      heading: "Neighborhood Cap Rates",
      rows: [
        { key: "avg_cap_rate", label: "Neighborhood Avg Cap Rate", value: fmtPct(neighborhoodAvg) },
        { key: "weighted_cap_rate", label: "Weighted Avg Cap Rate", value: fmtPct(neighborhoodWeighted) },
        { key: "median_cap_rate", label: "Median Cap Rate", value: fmtPct(neighborhoodMedian) },
        { key: "asset_class_cap_rate", label: `Avg Cap Rate, ${beds}`, value: fmtPct(assetClass) },
      ],
    },
    {
      heading: "This Home vs Neighborhood",
      rows: [
        { key: "rcr", label: "RCR", value: fmtRatio(capRate, neighborhoodAvg) },
        { key: "rcr_weighted", label: "Weighted RCR", value: fmtRatio(capRate, neighborhoodWeighted) },
        {
          key: "rcr_asset_class",
          label: `RCR vs ${beds}`,
          value: fmtRatio(capRate, assetClass),
          // Shown and LABELLED for the analyst rather than hidden: knowing the hood had too
          // few sales of this size to measure is itself the signal. The client never sees it
          // -- the share endpoint withholds a fallback, so `assetClass` arrives null there.
          note: assetClassIsFallback ? "hood-wide" : null,
        },
      ],
    },
  ]
    .map((g) => ({
      ...g,
      // A figure the database does not have is gone in both modes; a figure the analyst
      // unticked is gone only on the client's.
      rows: g.rows.filter((r) => r.value !== null && (editable || isRcrRowVisible(visibility, r.key))),
    }))
    .filter((g) => g.rows.length > 0);

  if (!groups.length) return null;

  return (
    <div style={{
      backgroundColor: "white",
      padding: narrow ? "16px 12px" : "24px",
      borderRadius: "8px",
      marginBottom: "24px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    }}>
      <h2 style={{
        fontSize: "20px", fontWeight: "600", color: "#333", marginBottom: editable ? "4px" : "16px",
        borderBottom: "2px solid #000", paddingBottom: "8px",
      }}>
        RCR &mdash; Relative Capital Return Analysis
      </h2>

      {editable && (
        <div style={{ fontSize: "12px", color: "#666", margin: "8px 0 16px", lineHeight: 1.5 }}>
          Untick a figure to leave it out of <strong>this listing&rsquo;s</strong> client view.
          Everything stays here either way.
        </div>
      )}

      <div style={{
        display: "grid",
        gridTemplateColumns: narrow ? "1fr" : "1fr 1fr",
        gap: narrow ? "20px" : "0 32px",
      }}>
        {groups.map(({ heading, rows }) => (
          <div key={heading}>
            <div style={{
              fontSize: "12px", fontWeight: "700", letterSpacing: "0.04em",
              textTransform: "uppercase", color: "#888", paddingBottom: "6px",
            }}>
              {heading}
            </div>
            {rows.map(({ key, label, value, note }) => {
              const shown = isRcrRowVisible(visibility, key);
              return (
                <div key={key} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  gap: "12px", padding: "8px 0", borderBottom: "1px solid #eee",
                  fontSize: narrow ? "14px" : "15px",
                  // Dimmed, not hidden: the row is still the analyst's to put back.
                  opacity: editable && !shown ? 0.4 : 1,
                }}>
                  <strong style={{ color: "#333" }}>{label}</strong>
                  <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ color: "#0066cc", fontWeight: "600" }}>{value}</span>
                    {note && (
                      <span style={{ color: "#999", fontWeight: "400", fontSize: "12px" }}>({note})</span>
                    )}
                    {editable && (
                      <input
                        type="checkbox"
                        checked={shown}
                        onChange={(e) => onVisibilityChange(key, e.target.checked)}
                        title={shown ? "Shown in the client view" : "Hidden from the client view"}
                        style={{ width: "15px", height: "15px", cursor: "pointer", margin: 0 }}
                      />
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <p style={{ marginTop: "14px", marginBottom: 0, fontSize: "13px", color: "#666" }}>
        RCR compares this home&rsquo;s cap rate with the neighborhood average &mdash; 1.00
        is exactly average, and higher is better.
      </p>
    </div>
  );
}
